import { useEffect, useMemo, useRef, useState } from 'react';
import { Tick, Spinner, SearchNormal1, Cross } from '@/components/icons';
import { Button } from '@/components/ui/button';
import FilterPopover from '@/components/ui/FilterPopover';
import { Input } from '@/components/ui/input';
import { useModalContext } from '@/components/ui/modal-provider';
import { useCidSearch } from '@/hooks/use-cid-search';
import { highlightMatch } from '@/lib/highlight-match';
import type { CIDOption } from '@/types/report';
import type { ChangeEvent } from 'react';

interface CIDSearchModalProps {
    selectedCIDs: CIDOption[];
    onApply: (cids: CIDOption[]) => void;
}

export default function CIDSearchModal({ selectedCIDs, onApply }: CIDSearchModalProps) {
    const { closeModal } = useModalContext();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSource, setSelectedSource] = useState<'cid10' | 'cid11'>('cid11');
    const [draftSelection, setDraftSelection] = useState<CIDOption[]>(selectedCIDs);
    const [isSelectionExpanded, setIsSelectionExpanded] = useState(false);

    const resultsContainerRef = useRef<HTMLDivElement | null>(null);

    const {
        entries: searchResults,
        isLoading: isSearching,
        isFetchingNextPage,
        hasNextPage,
        activeQuery,
        isQueryTooShort,
        sentinelRef,
    } = useCidSearch({
        query: searchQuery,
        source: selectedSource,
    });

    useEffect(() => {
        setDraftSelection(selectedCIDs);
        setSelectedSource('cid11');
        setIsSelectionExpanded(false);
    }, [selectedCIDs]);

    // Jump back to the top of the list whenever the search changes so the most
    // relevant (top-ranked) result is what the user sees first.
    useEffect(() => {
        resultsContainerRef.current?.scrollTo({ top: 0 });
    }, [activeQuery, selectedSource]);

    const displayOptions = useMemo(
        () =>
            searchResults.map((entry) => ({
                id: entry.code,
                code: entry.code,
                description: entry.description,
                label: `${entry.code} - ${entry.description}`,
            })),
        [searchResults],
    );

    const selectedIds = useMemo(
        () => new Set(draftSelection.map((cid) => cid.id)),
        [draftSelection],
    );

    const visibleSelectedCIDs = isSelectionExpanded ? draftSelection : draftSelection.slice(0, 4);

    const hiddenSelectedCount = Math.max(draftSelection.length - 4, 0);

    const toggleCID = (cid: CIDOption) => {
        setDraftSelection((current) => {
            const isSelected = current.some((item) => item.id === cid.id);

            if (isSelected) {
                return current.filter((item) => item.id !== cid.id);
            }

            return [...current, cid];
        });
    };

    const handleClose = () => {
        closeModal();
    };

    const handleApply = () => {
        onApply(draftSelection);
        closeModal();
    };

    const hasNoResults = !isSearching && !isQueryTooShort && displayOptions.length === 0;

    const sourceOptions = [
        { value: 'cid11', label: 'CID-11' },
        { value: 'cid10', label: 'CID-10' },
    ];

    return (
        <div className="flex size-full min-h-0 flex-col">
            <div className="shrink-0 space-y-2 px-6 pb-4">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <SearchNormal1 className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            autoFocus
                            placeholder="Digite o CID ou descricao..."
                            value={searchQuery}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                setSearchQuery(event.target.value)
                            }
                            className="rounded-lg border-border-strong pl-9"
                        />
                        {isSearching && (
                            <Spinner className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                        )}
                    </div>
                    <FilterPopover
                        options={sourceOptions}
                        value={selectedSource}
                        onApply={(value) => setSelectedSource(value as 'cid10' | 'cid11')}
                        showAllOption={false}
                        isFiltered={selectedSource !== 'cid11'}
                        label="Fonte"
                        placeholder="Selecione a classificacao"
                    />
                </div>

                {draftSelection.length > 0 && (
                    <div className="space-y-2 pt-1">
                        <div className="flex flex-wrap gap-2">
                            {visibleSelectedCIDs.map((cid) => (
                                <div
                                    key={cid.id}
                                    className="flex max-w-full items-center gap-1 rounded-full border border-border bg-muted px-3 py-1.5 text-sm"
                                >
                                    <span className="min-w-0 break-words">{cid.label}</span>
                                    <button
                                        type="button"
                                        onClick={() => toggleCID(cid)}
                                        className="shrink-0 text-muted-foreground transition-colors hover:text-muted-foreground"
                                        aria-label={`Remover ${cid.label}`}
                                    >
                                        <Cross className="size-3" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {draftSelection.length > 4 && (
                            <button
                                type="button"
                                onClick={() => setIsSelectionExpanded((current) => !current)}
                                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                            >
                                {isSelectionExpanded
                                    ? 'Mostrar menos'
                                    : `Ver mais ${hiddenSelectedCount}`}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div
                ref={resultsContainerRef}
                className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-2 pr-4"
            >
                {isQueryTooShort ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        Digite ao menos 2 caracteres para buscar.
                    </div>
                ) : null}

                {isSearching ? (
                    <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
                        <Spinner className="size-4 animate-spin" />
                        <span className="text-sm">Buscando CIDs...</span>
                    </div>
                ) : null}

                {hasNoResults ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        Nenhum CID encontrado para a busca informada.
                    </div>
                ) : null}

                {!hasNoResults &&
                    displayOptions.map((cid) => {
                        const isSelected = selectedIds.has(cid.id);

                        return (
                            <div key={cid.id} className="space-y-1">
                                <button
                                    type="button"
                                    onClick={() => toggleCID({ id: cid.id, label: cid.label })}
                                    role="checkbox"
                                    aria-checked={isSelected}
                                    className={`flex w-full items-start gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-border bg-card hover:border-border-strong'
                                    }`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm border transition-colors ${
                                            isSelected
                                                ? 'border-blue-500 bg-blue-500 text-white'
                                                : 'border-border-strong bg-card text-transparent'
                                        }`}
                                    >
                                        <Tick className="size-3.5" />
                                    </span>

                                    <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                        <div className="shrink-0 font-medium text-foreground">
                                            {highlightMatch(cid.code, activeQuery)}
                                        </div>
                                        <div className="shrink-0 text-muted-foreground">-</div>
                                        <div className="min-w-0 flex-1 break-words text-sm text-muted-foreground">
                                            {highlightMatch(cid.description, activeQuery)}
                                        </div>
                                    </div>
                                </button>
                            </div>
                        );
                    })}

                {!isSearching && !hasNoResults && hasNextPage && (
                    <div ref={sentinelRef} className="py-1" />
                )}

                {isFetchingNextPage && (
                    <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
                        <Spinner className="size-4 animate-spin" />
                        <span className="text-sm">Carregando mais CIDs...</span>
                    </div>
                )}
            </div>

            <div className="shrink-0 border-t bg-card px-6 py-4">
                <div className="flex items-center justify-end gap-3">
                    <Button variant="outline" onClick={handleClose}>
                        Cancelar
                    </Button>
                    <Button onClick={handleApply}>Aplicar ({draftSelection.length})</Button>
                </div>
            </div>
        </div>
    );
}
